// Utility functions for consistent ButtonGroup styling across the application
// This ensures all ButtonGroups follow the design system

/**
 * Returns consistent styling for active and inactive button states in ButtonGroups
 * @param {Object} theme - Material-UI theme object
 * @returns {Object} - Object containing active and inactive class styles
 */
export const getButtonGroupStyles = (theme) => ({
  active: {
    fontWeight: "bold",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText} !important`,
    borderColor: `${theme.palette.primary.main} !important`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.dark} !important`,
      borderColor: `${theme.palette.primary.dark} !important`,
    }
  },
  inactive: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(0, 51, 102, 0.04)',
    }
  }
});

/**
 * Returns consistent styling for secondary ButtonGroup variant (using secondary color)
 * @param {Object} theme - Material-UI theme object
 * @returns {Object} - Object containing active and inactive class styles with secondary colors
 */
export const getSecondaryButtonGroupStyles = (theme) => ({
  active: {
    fontWeight: "bold",
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: `${theme.palette.secondary.contrastText} !important`,
    borderColor: `${theme.palette.secondary.main} !important`,
    '&:hover': {
      backgroundColor: `${theme.palette.secondary.dark} !important`,
      borderColor: `${theme.palette.secondary.dark} !important`,
    }
  },
  inactive: {
    fontWeight: "bold",
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 193, 7, 0.04)', // Secondary color with low opacity
    }
  }
});

/**
 * Returns the appropriate className for a button based on its active state
 * @param {boolean} isActive - Whether the button is in active state
 * @param {Object} classes - Classes object from makeStyles
 * @returns {string} - The appropriate className
 */
export const getButtonGroupClassName = (isActive, classes) => {
  return isActive ? classes.active : classes.inactive;
};

/**
 * Hook to create consistent ButtonGroup styles using makeStyles
 * @param {Object} theme - Material-UI theme object
 * @param {string} variant - 'primary' or 'secondary' variant
 * @returns {Object} - Classes object with active and inactive styles
 */
export const useButtonGroupStyles = (makeStyles, variant = 'primary') => {
  return makeStyles((theme) => {
    if (variant === 'secondary') {
      return getSecondaryButtonGroupStyles(theme);
    }
    return getButtonGroupStyles(theme);
  });
};


export const getFilterModalButtonSx = (isSelected) => {
  if (isSelected) {
    return {
      backgroundColor: "#1976d2",
      color: "#ffffff !important",
      border: "1px solid #1976d2",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "#1565c0",
        border: "1px solid #1565c0",
      },
    };
  }
  return {
    backgroundColor: "rgba(158, 158, 158, 0.1)",
    color: "#757575 !important",
    border: "1px solid #bdbdbd",
    fontWeight: 500,
    borderRadius: "16px",
    fontSize: "0.875rem",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "rgba(158, 158, 158, 0.2)",
      border: "1px solid #bdbdbd",
    },
  };
};
